import React from "react";
import { useAppSelector, useIsMobile } from "../app/hooks";
import { selectLabels } from "../slices/language";
import { selectHasUserPropertySetting, selectUserRole } from "../slices/user";

const useMenu = () => {
  const labels = useAppSelector(selectLabels);
  const userRole = useAppSelector(selectUserRole);
  const isMobile = useIsMobile();
  const hasUserAccessGroupsPerms = useAppSelector(
    selectHasUserPropertySetting("user_access_groups")
  );
  const hasUserAccessGroups = hasUserAccessGroupsPerms && userRole === "host";

  const [fixedMenu, setFixedMenu] = React.useState<
    | {
        id: string;
        label: string;
        icon: string;
        show: boolean;
        route: string;
        children: {
          label: any;
          route: string;
          icon: string;
        }[];
      }[]
  >([]);

  const [openSubmenu, setOpenSubmenu] = React.useState<string[]>([]);

  const menu = React.useMemo(() => {
    return [
      {
        id: "log",
        label: labels.log,
        icon: "material-symbols:checkbook",
        show: userRole === "guard",
        route: "/logs",
        children: [],
      },
      {
        id: "properties",
        label: labels.properties,
        icon: "fa-solid:building",
        show: userRole === "ciso",
        route: "/properties",
        children: [],
      },
      {
        id: "access_points",
        label: labels.access_points,
        icon: "fa-solid:door-open",
        show: userRole === "ciso",
        route: "/access-points",
        children: [],
      },
      {
        id: "access_groups",
        label: labels.access_groups,
        icon: "ri:door-lock-fill",
        show: userRole === "ciso",
        route: "/access-groups",
        children: [],
      },
      {
        id: "admins",
        label: labels.admins,
        icon: "fa-solid:user-cog",
        show: userRole === "ciso",
        route: "/network-admins",
        children: [],
      },
      {
        id: "access_keys",
        label: labels.access_keys,
        icon: "fa-solid:envelope-open-text",
        show: userRole === "ciso" ? false : true,
        route: "/access-keys",
        children: [],
      },
      {
        id: "logbook",
        label: labels.logbook,
        icon: "fa6-solid:book",
        show: userRole === "ciso" ? false : true,
        route: "/logbook",
        children: [],
      },
      {
        id: "users",
        label: labels.users,
        icon: "fa-solid:users",
        show: userRole === "host",
        route: "/users",
        children: [],
      },
      {
        id: "users",
        label: labels.users,
        icon: "fa-solid:users",
        show: userRole === "admin" && isMobile,
        route: "/users",
        children: [],
      },
      {
        id: "users",
        label: labels.units,
        icon: "heroicons:squares-2x2-20-solid",
        show: userRole === "admin" && isMobile,
        route: "/units",
        children: [],
      },
      {
        id: "access_groups",
        label: labels.access_groups,
        icon: "ri:door-lock-fill",
        show: hasUserAccessGroups && !isMobile,
        route: "/access-groups",
        children: [],
      },
      {
        id: "settings",
        label: labels.settings,
        icon: "fa-solid:cog",
        show: userRole === "admin" && !isMobile,
        route: "",
        children: [
          {
            label: labels.access_points,
            route: "/access-points",
            icon: "fa-solid:door-open",
          },
          {
            label: labels.access_groups,
            route: "/access-groups",
            icon: "ri:door-lock-fill",
          },
          {
            label: labels.units,
            route: "/units",
            icon: "heroicons:squares-2x2-20-solid",
          },
          {
            label: labels.users,
            route: "/host-users",
            icon: "fa-solid:user",
          },
          {
            label: labels.security_guards,
            route: "/security-guards",
            icon: "fa-solid:user-shield",
          },
          {
            label: labels.admins,
            route: "/network-admins",
            icon: "fa-solid:user-cog",
          },
        ],
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels, userRole, isMobile, hasUserAccessGroupsPerms]);

  const toggleSubMenu = (item: string) => {
    const newOpenSubmenu = [...openSubmenu];
    if (newOpenSubmenu.includes(item)) {
      const index = newOpenSubmenu.indexOf(item);
      newOpenSubmenu.splice(index, 1);
    } else {
      newOpenSubmenu.push(item);
    }
    setOpenSubmenu(newOpenSubmenu);
  };

  React.useEffect(() => {
    setFixedMenu(menu);
  }, [menu]);

  return { fixedMenu, toggleSubMenu, openSubmenu };
};

export default useMenu;
